import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Check, AlertCircle, ImageIcon } from 'lucide-react';
import { getExamSubmissionDetails } from './api';

const calculateTotalMaxMarks = (examData) => {
  return examData.examQuestion.reduce((total, section) => 
    total + section.questions.reduce((sectionTotal, question) => sectionTotal + question.marks, 0), 0
  );
};

const calculateTotalObtainedMarks = (examData) => {
  return examData.examQuestion.reduce((total, section) => 
    total + section.questions.reduce((sectionTotal, question) => sectionTotal + (question.obtainedMarks || 0), 0), 0
  );
};

export default function SubmittedExamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submissionData, setSubmissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExamDetails = async () => {
      try {
        setLoading(true);
        const response = await getExamSubmissionDetails(id);
        console.log('API Response:', response); // Log the entire response

        if (response.statusCode === 200 && response.body) {
          const parsedBody = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
          console.log('Parsed body:', parsedBody); // Log the parsed body

          if (parsedBody.data) {
            setSubmissionData(parsedBody.data);
            setError(null);
          } else {
            throw new Error('No data found in the response');
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Error fetching submitted exam details:', err);
        setError(`Failed to load submitted exam details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExamDetails();
  }, [id]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex items-center justify-center min-h-screen text-red-600">{error}</div>;
  }

  if (!submissionData) {
    return <div className="flex items-center justify-center min-h-screen">No exam data found.</div>;
  }

  const renderAnswer = (question) => {
    if (question.image) {
      return (
        <div className="mt-2">
          <p className="font-medium">Your Answer (Image):</p>
          <div className="mt-2 max-w-md">
            <img src={question.image} alt="Submitted answer" className="w-full h-auto rounded-lg shadow-md" />
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2">
        <p className="font-medium">Your Answer:</p>
        <p className="ml-4 whitespace-pre-wrap">{question.user_answer || 'No answer provided'}</p>
      </div>
    );
  };

  const renderEvaluation = (question) => {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-lg mb-2">Evaluation</h4>
        {question.remarks ? (
          <>
            <p className="text-green-600 font-medium mb-2">Evaluated</p>
            <p className="font-medium">Remarks:</p>
            <p className="ml-4 text-gray-700">{question.remarks}</p>
            <p className="mt-2 font-medium">
              Marks Obtained: <span className="text-blue-600">{question.obtainedMarks} / {question.marks}</span>
            </p>
          </>
        ) : (
          <p className="text-yellow-600 font-medium">Not evaluated yet</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/exams')}
          className="mb-6 flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="text-lg font-medium">Back to Exams</span>
        </button>
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className="p-6 sm:p-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{submissionData.exam_topic || 'Untitled Exam'}</h1>
            <p className="text-xl text-gray-600 mb-4">Submitted Exam</p>
            <div className="flex items-center text-green-600 mb-8">
              <Check className="w-5 h-5 mr-2" />
              <span>Status: {submissionData.status}</span>
            </div>
            
            {submissionData.examQuestion.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.name}</h2>
                <div className="space-y-8">
                  {section.questions.map((question, questionIndex) => (
                    <div key={questionIndex} className="bg-white p-6 rounded-xl shadow-lg mb-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                          <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3">
                            {questionIndex + 1}
                          </span>
                          Question
                        </h3>
                        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                          {question.type}
                        </span>
                      </div>
                      <div className="text-lg text-gray-700 mb-4">
                        {question.text}
                      </div>
                      {question.type === 'multiple-choice' && question.options && (
                        <div className="mb-4">
                          <p className="font-medium">Options:</p>
                          <ul className="list-disc list-inside ml-4">
                            {question.options.map((option, optionIndex) => (
                              <li key={optionIndex} className="text-gray-700">{option}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {renderAnswer(question)}
                      {renderEvaluation(question)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gray-50 px-6 py-4 sm:px-10 sm:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-6 h-6 text-blue-500 mr-3" />
                <span className="text-gray-600">Exam submitted successfully</span>
              </div>
              <div className="text-lg font-medium">
                Total Marks: <span className="text-green-600">{calculateTotalObtainedMarks(submissionData)}</span> / <span className="text-blue-600">{calculateTotalMaxMarks(submissionData)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

